import { createSelector } from 'reselect'
import type { ExperienceListFilters } from 'actions/ExperienceActions'
import buildLoyaltyLoungePassFilters from 'lib/experiences/buildLoyaltyLoungePassFilters'
import getFlightDetailsByOrderItems from 'lib/flights/getFlightDetailsByOrderItems'
import { getExperienceListKey } from 'lib/experiences/experienceUtils'
import { getSortedOrdersByDeparture } from 'selectors/orderSelectors'
import { getClosestAirport } from 'selectors/flightsSelectors'
import { isSpoofed } from 'selectors/featuresSelectors'
import getIsCurrentRegionUserLoyaltyRegion from 'loyalty/selectors/getIsCurrentRegionUserLoyaltyRegion'
import { isOrderCancelled, isStandaloneOrder } from 'lib/order/orderUtils'
import isUpcoming from 'lib/order/isUpcoming'
import { isOrderInternationalTrip } from './getOrderIsLoyaltyInternationalTrip'

export const getPreQualifyingOrdersWithLoungeFilters = createSelector(
  getSortedOrdersByDeparture,
  (state: App.State) => state.loyalty.account.data?.benefits,
  (state: App.State) => state.orders.flightDetails,
  (state: App.State) => state.cruise.cruiseOfferSnapshot,
  (state: App.State) => getIsCurrentRegionUserLoyaltyRegion(state),
  (state: App.State) => state.geo.airports,
  (state: App.State) => state.offer.offers,
  (state: App.State) => getClosestAirport(state),
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => state.auth.account.recentlyUsedAirportCode,
  (state: App.State) => state.experience.experienceLists,
  (state: App.State) => isSpoofed(state),
  (
    allOrders,
    benefits,
    flightDetails,
    cruiseOfferSnapshots,
    isLoyaltyRegion,
    airports,
    offers,
    closestAirport,
    regionCode,
    recentlyUsedAirportCode,
    experienceLists,
    spoofed,
  ): { preQualifyingOrders: Array<App.Order>, filtersByOrderId: Record<string, Array<ExperienceListFilters>> } => {
    const empty = { preQualifyingOrders: [], filtersByOrderId: {} }

    if (!isLoyaltyRegion) return empty

    const loungePassBenefit = benefits?.find(b => b.code === 'lounge_pass')
    if (!loungePassBenefit || (!loungePassBenefit.unlimited && !loungePassBenefit.remaining)) return empty

    const preQualifyingOrders = allOrders.data.filter(order =>
      !isOrderCancelled(order) &&
      !isStandaloneOrder(order, 'merchandise') &&
      isUpcoming(order) &&
      !!order.loyaltyDetails.tier &&
      isOrderInternationalTrip(order, flightDetails, cruiseOfferSnapshots),
    )

    const filtersByOrderId: Record<string, Array<ExperienceListFilters>> = {}
    for (const order of preQualifyingOrders) {
      filtersByOrderId[order.id] = buildLoyaltyLoungePassFilters({
        bedbankItems: order.bedbankItems,
        tourItems: order.tourItems,
        hotelItems: order.items,
        flightItems: order.flightItems,
        cruiseItems: order.cruiseItems,
        cruiseOfferSnapshots,
        experienceLists,
        airports,
        regionCode,
        isSpoofed: spoofed,
        flightDetailsByOrderItems: getFlightDetailsByOrderItems(order, flightDetails),
        offers,
        closestAirport,
        recentlyUsedAirportCode,
      })
    }

    return { preQualifyingOrders, filtersByOrderId }
  },
)

const getUpcomingOrdersEligibleForLoungePassRedemption = createSelector(
  getPreQualifyingOrdersWithLoungeFilters,
  (state: App.State) => state.experience.experienceLists,
  ({ preQualifyingOrders, filtersByOrderId }, experienceLists): Array<App.Order> => {
    return preQualifyingOrders.filter(order => {
      const filters = filtersByOrderId[order.id] ?? []
      if (!filters.length) return false
      const fetching = filters.some(f => experienceLists[getExperienceListKey(f)]?.fetching)
      const count = filters.reduce((acc, f) => acc + (experienceLists[getExperienceListKey(f)]?.total ?? 0), 0)
      return !fetching && count > 0
    })
  },
)

export default getUpcomingOrdersEligibleForLoungePassRedemption
